import { Injectable, signal } from '@angular/core';
import {
  signIn,
  signOut,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
  getCurrentUser,
  type AuthUser,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { from, Observable } from 'rxjs';
import { IMPERSONATION_STORAGE_KEY } from '../models/impersonation.model';

export interface SignInResult {
  requiresNewPassword: boolean;
}

export interface CustomAuthUser extends AuthUser {
  name?:string;
  email?:string;
}
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _currentUser = signal<CustomAuthUser | null>(null);

  readonly currentUser = this._currentUser.asReadonly();

  /**
   * Signs in, transparently recovering from a stale Amplify session.
   *
   * Amplify throws `UserAlreadyAuthenticatedException` when a Cognito session
   * is still alive in localStorage. The `guestGuard` on `/login` keeps that
   * from happening in the normal flow, but it cannot cover the race where a
   * session appears AFTER the guard ran (another tab signing in, or a token
   * refreshed mid-form). Left unhandled it surfaces as a raw Amplify error and
   * the user is stuck on the login screen with no way out but editing the URL.
   *
   * Recovery = sign the stale session out and retry with the credentials the
   * user just typed. Signing in as a DIFFERENT user therefore replaces the
   * live session, which is the expected reading of "I typed these credentials".
   */
  async signIn(username: string, password: string): Promise<SignInResult> {
    let result: Awaited<ReturnType<typeof signIn>>;
    try {
      result = await signIn({ username, password });
    } catch (err: unknown) {
      if ((err as Error)?.name !== 'UserAlreadyAuthenticatedException') throw err;
      await this.signOut();
      result = await signIn({ username, password });
    }

    if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { requiresNewPassword: true };
    }
    await this.getAndSetCurrentUser();
    return { requiresNewPassword: false };
  }

  private async getAndSetCurrentUser() {
    const user: CustomAuthUser = await getCurrentUser();
    const attrs = await fetchUserAttributes();
    user.email = attrs.email;
    user.name = attrs.name;

    this._currentUser.set(user);

    return user;
  }

  async confirmNewPassword(newPassword: string): Promise<void> {
    await confirmSignIn({ challengeResponse: newPassword });
    const user = await getCurrentUser();
    this._currentUser.set(user);
  }

  async signOut(): Promise<void> {
    this.clearScopedToken();
    this.clearImpersonationKeys();
    await signOut();
    this._currentUser.set(null);
  }

  /**
   * Step 1 of the forgot-password flow. Cognito emails a confirmation code to
   * the user. Works for any pool user (Cognito does not distinguish by role).
   */
  async requestPasswordReset(username: string): Promise<void> {
    await resetPassword({ username });
  }

  /**
   * Step 2 of the forgot-password flow. Validates the emailed code and sets the
   * new password. On success the user can sign in with the new password.
   */
  async confirmPasswordReset(
    username: string,
    confirmationCode: string,
    newPassword: string,
  ): Promise<void> {
    await confirmResetPassword({ username, confirmationCode, newPassword });
  }

  /**
   * Token priority: impersonation > scoped > Amplify (BR-AUTH-03). The
   * impersonation key is read here directly (not through
   * `ImpersonationService`) so this service stays free of HTTP dependencies
   * and the interceptor never forms a cycle.
   */
  async getAccessToken(): Promise<string | null> {
    const impersonation = this.getImpersonationToken();
    if (impersonation) return impersonation;
    const scoped = this.getScopedToken();
    if (scoped) return scoped;
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Returns ONLY the raw Amplify/Cognito access token — never the scoped or
   * impersonation token. Unlike `getAccessToken()`, this ignores the priority
   * order entirely.
   *
   * Used by `ScopedTokenRefreshService` to re-mint an expired scoped token:
   * that call MUST be authenticated with the still-valid Cognito session, not
   * with the very token that is being replaced.
   */
  async getCognitoAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() ?? null;
    } catch {
      return null;
    }
  }

  // Stored in localStorage (NOT sessionStorage) so the selected tenant is
  // shared across browser tabs — matching Amplify's Cognito session, which
  // also lives in localStorage. Otherwise a link opened in a new tab keeps the
  // login but loses the tenant, re-prompting /select-tenant. Cleared on
  // signOut() via clearScopedToken().
  setScopedToken(token: string): void {
    localStorage.setItem('niko_tenant_session', token);
  }

  getScopedToken(): string | null {
    return localStorage.getItem('niko_tenant_session');
  }

  setScopedTenantName(name: string): void {
    localStorage.setItem('niko_tenant_name', name);
  }

  getScopedTenantName(): string {
    return localStorage.getItem('niko_tenant_name') ?? '';
  }

  clearScopedToken(): void {
    localStorage.removeItem('niko_tenant_session');
    localStorage.removeItem('niko_tenant_name');
  }

  getImpersonationToken(): string | null {
    return localStorage.getItem(IMPERSONATION_STORAGE_KEY.SESSION);
  }

  // Only on signOut(). Leaving an impersonation while staying signed in goes
  // through `ImpersonationService.exit()`, which keeps the scoped token.
  private clearImpersonationKeys(): void {
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY.SESSION);
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY.META);
  }

  async loadCurrentUser(): Promise<CustomAuthUser | null> {
    try {
      return await this.getAndSetCurrentUser();
    } catch {
      this._currentUser.set(null);
      return null;
    }
  }

  /**
   * Returns the raw access token payload (decoded JWT claims).
   * Useful for checking Cognito groups.
   */
  async getTokenPayload(): Promise<Record<string, unknown> | null> {
    try {
      const session = await fetchAuthSession();
      return (session.tokens?.accessToken?.payload as Record<string, unknown>) ?? null;
    } catch {
      return null;
    }
  }

  isAuthenticated(): Observable<boolean> {

    return from(
      fetchAuthSession()
        .then((s) => {
          const res = s.tokens?.accessToken !== undefined
          return res;
        })
        .catch(() => false),
    );
  }
}
